import { mdiInformationOutline, mdiRefresh } from "@mdi/js";
import Icon from "@mdi/react";
import { FunctionComponent, useEffect, useState } from "react";
import { useOrderCreateCtx } from "../../../../context_providers/order_create_context";
import { getExchangeRate } from "../../../../erp/purchase_order/purchase_order_create/service/purchase_order_repository";
import { currencies } from "../../../../erp/accounts/models/party_model";
import LoadingWidget from "../../content_panel/ui/new_components/common/loading_spinner";
import {
  ToastType,
  useToast,
} from "../../../../context_providers/toast/toast_ctx";

interface CurrencySelectorProps {}

const CurrencySelector: FunctionComponent<CurrencySelectorProps> = () => {
  const { order, setOrder } = useOrderCreateCtx();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);

  const getCurrencyExchangeRate = async (fromCurrency: string) => {
    try {
      setLoading(true);
      const formattedDate = new Date().toISOString().split("T")[0];

      const exchangeRate = await getExchangeRate({
        from: fromCurrency,
        to: "INR",
        date: formattedDate,
      });

      if (exchangeRate !== null && exchangeRate > 0) {
        setOrder((o) => ({
          ...o,
          currency_to_inr: exchangeRate,
        }));
        showToast({
          type: ToastType.success,
          text: `Exchange rate fetched: 1 ${fromCurrency} = ₹${exchangeRate}`,
        });
      } else {
        showToast({
          type: ToastType.error,
          text: "Exchange rate could not be retrieved.",
        });
      }
    } catch (error) {
      console.error("Error getting exchange rate:", error);
      showToast({
        type: ToastType.error,
        text: "Error fetching exchange rate.",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (order.currency?.short_name && order.currency.short_name !== "INR") {
      if (!order.currency_to_inr || order.currency_to_inr <= 0) {
        getCurrencyExchangeRate(order.currency.short_name);
      }
    }
  }, [order.currency?.short_name]);

  return (
    <div className="bg-white rounded-md shadow p-3 md:p-5 mb-5">
      <h3 className="font-semibold mb-3 text-sm md:text-base">
        Currency Settings
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
        {/* Currency Selector */}
        <div>
          <label
            htmlFor="order-create-currency"
            className="block mb-1 font-medium text-xs md:text-sm"
          >
            Currency
          </label>
          <select
            id="order-create-currency"
            required
            className="p-2 text-xs md:text-sm focus:outline-none border rounded w-full"
            value={order.currency ? JSON.stringify(order.currency) : ""}
            onChange={(e) => {
              const newCurrency = JSON.parse(e.target.value);
              setOrder((o) => {
                const updated = {
                  ...o,
                  currency: newCurrency,
                  currency_to_inr:
                    newCurrency.short_name === "INR" ? 1 : o.currency_to_inr,
                };
                return updated;
              });
              if (newCurrency.short_name !== "INR") {
                getCurrencyExchangeRate(newCurrency.short_name);
              }
            }}
          >
            <option value="" hidden>
              Select Currency
            </option>
            {Object.values(currencies).map((currency, idx) => (
              <option key={idx} value={JSON.stringify(currency)}>
                {currency.formal_name} ({currency.symbol})
              </option>
            ))}
          </select>
        </div>

        {/* Exchange Rate Section */}
        {order.currency?.short_name !== "INR" && (
          <div>
            <div className="flex justify-between items-center">
              <label
                htmlFor="order-create-exchange-rate"
                className="block mb-1 font-medium text-xs md:text-sm"
              >
                Exchange Rate (₹ per {order.currency?.short_name})
              </label>
              <button
                id="order-create-exchange-rate-refresh-btn"
                type="button"
                onClick={() =>
                  getCurrencyExchangeRate(order.currency!.short_name)
                }
                className="text-xs md:text-sm flex items-center gap-1 text-blue-600 hover:text-blue-800"
              >
                <Icon path={mdiRefresh} size={0.7} />
                Refresh
              </button>
            </div>

            <input
              id="order-create-exchange-rate"
              type="number"
              step="0.01"
              placeholder={`Exchange rate for ${order.currency?.short_name} → INR`}
              value={order.currency_to_inr || ""}
              onChange={(e) => {
                setOrder((o) => ({
                  ...o,
                  currency_to_inr: Number(e.target.value),
                }));
              }}
              className="p-2 text-xs md:text-sm focus:outline-none border rounded w-full"
            />
            <div className="flex gap-1 text-gray-500 text-xs mt-2 items-center">
              <Icon path={mdiInformationOutline} size={0.7} />
              Enter the INR equivalent of 1 {order.currency?.short_name}. (Eg:
              ₹83 = $1)
            </div>
            {loading && <LoadingWidget loading />}
          </div>
        )}
      </div>

      {/* Show converted currency info */}
      <div className="mt-4 p-3 border rounded bg-gray-50 text-xs md:text-sm text-gray-700">
        <p>
          Billing Currency:{" "}
          <strong>
            {order.currency?.formal_name} ({order.currency?.symbol})
          </strong>
        </p>
        {order.currency?.short_name !== "INR" && (
          <p>
            Exchange Rate: 1 {order.currency?.short_name} = ₹
            {order.currency_to_inr || "?"}
          </p>
        )}
      </div>
    </div>
  );
};

export default CurrencySelector;
